import API from "../constants/api.constant";
import {responseErrorHandling} from "../helper/common.helper";
import {UploadNcnndaAgreementData, UploadSignatureData} from "../validation/business.validation";
import axiosInstance from "./axiosInstance";
import { jwtDecode } from "jwt-decode";

// Function to create a new business
// This function sends a POST request to create a new business with the provided data
export const createBusiness = async (businessData: Object) => {
  try {
    const response = await axiosInstance.post(
      API.CREATE_BUSINESS_ENDPOINT,
      businessData
    );
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
};

// Function to log in a user
// This function sends a POST request to log in a user with the provided credentials
export const loginUser = async (credentials: Object) => {
  try {
    const response = await axiosInstance.post(
      API.LOGIN_USER_ENDPOINT,
      credentials
    );
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
};

// store access token in local storage
export const storeAccessToken = async (token: string) => {
  localStorage.setItem("accessToken", token);
  return token;
};

// retrieve access token from local storage
export const getAccessToken = () => {
  const token = localStorage.getItem("accessToken");
  if (!token) {
    return null;
  }
  return token;
};

// remove access token from local storage
export const removeAccessToken = (): Promise<void> => {
  return new Promise((resolve) => {
    localStorage.removeItem("accessToken");
    resolve();
  });
};

// Function to check if the user is authenticated
export const isAuthenticated = () => {
  const token = getAccessToken();
  return !!token; // Returns true if token exists, false otherwise
};

// Function to get user data
export const getLoginUserData = async () => {
  try {
    const token = getAccessToken();
    if (!token) {
      throw new Error("No access token found");
    }
    // Decode the JWT token
    const decoded:any = jwtDecode(token);
    return decoded;
  } catch (error: any) {
    responseErrorHandling(error)
  }
};

// logout user
export const logoutUser = async () => {
  try {
    await axiosInstance.post(API.LOGOUT_USER_ENDPOINT);
  } catch (error) {
    // Still clear the local token if the server-side logout audit call fails.
  }
  await removeAccessToken()
  return true;
}

// Function to verify invitation token
export const verifyInvitationToken = async (data: object) => {
  try {
    const response = await axiosInstance.post(API.VERIFY_TOKEN, data);
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
}
// register coOwner
export const registerCoOwner = async (coOwnerData: Object) => {
  try {
    const response = await axiosInstance.post(API.CO_OWNER_ACCOUNT_REGISTER, coOwnerData);
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
}

export const registerEmployee = async (employee: Object) => {
  try {
    const response = await axiosInstance.post(API.REGISTER_EMPLOYEE, employee);
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
}

export const registerCustomer = async (customer: Object) => {
  try {
    const response = await axiosInstance.post(API.REGISTER_CUSTOMER, customer);
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
}

export const uploadSignature = async (data: UploadSignatureData) => {
  try {
    const response = await axiosInstance.put(
      API.USER_UPLOAD_SIGNATURE_ENDPOINT + `/${data.id}`,
      { signature: data.signature }
    );
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
};

export const uploadNcnndaAgreement = async (data: UploadNcnndaAgreementData) => {
  try {
    const response = await axiosInstance.put(
      API.USER_UPLOAD_NCNDA_AGREEMENT_ENDPOINT + `/${data.id}`,
      {
        signature: data.signature,
        agreementPdfPath: data.agreementPdfPath,
      }
    );
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
};

export const registerBanker = async (banker: Object) => {
  try {
    const response = await axiosInstance.post(API.REGISTER_BANKER, banker);
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
}

// Forgot Password - Send Reset Email
export const sendResetEmail = async (email: string) => {
  try {
    const response = await axiosInstance.post(API.SEND_RESET_EMAIL, { email });
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
}

// Forgot Password - Verify OTP
export const verifyOTP = async (email: string, otp: string) => {
  try {
    const response = await axiosInstance.post(API.VERIFY_OTP, { email, otp });
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
}

// Registration - Send Email Verification OTP
export const sendVerificationEmail = async (email: string) => {
  try {
    const response = await axiosInstance.post(API.SEND_VERIFICATION_EMAIL, { email });
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
}

// Registration - Verify Email OTP
export const verifyEmailOTP = async (email: string, otp: string) => {
  try {
    const response = await axiosInstance.post(API.VERIFY_EMAIL_OTP, { email, otp });
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
}

// Forgot Password - Reset Password
export const resetPassword = async (email: string, otp: string, newPassword: string) => {
  try {
    const response = await axiosInstance.post(API.RESET_PASSWORD, { email, otp, newPassword });
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
}
